import { SearchIcon } from "lucide-react";
import { useState } from "react";

import { Spinner } from "@/web/components/primitives/spinner";
import type { AddressSuggestion } from "@/web/lib/geocoding";
import { cn } from "@/web/lib/styling";
import { searchedExternalId } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/constants";
import type { ExplorePin } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/types";
import { useAddressSearch } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/useAddressSearch";

/** Resolve a Mapbox address suggestion into a searched `ExplorePin`. */
function pinFromSuggestion(s: AddressSuggestion): ExplorePin {
  return {
    key: searchedExternalId(s.id),
    category: "offMarket",
    latitude: s.latitude,
    longitude: s.longitude,
    address: {
      addressLine1: s.addressLine1,
      addressLine2: undefined,
      city: s.city,
      state: s.state,
      zip: s.zip,
      countryCode: s.countryCode,
    },
    source: { kind: "searched", externalId: searchedExternalId(s.id) },
  };
}

export function ExploreSearchBar({
  onSelect,
  className,
}: {
  /** Fired when the user picks a suggestion; receives the resolved searched pin. */
  onSelect: (pin: ExplorePin) => void;
  className?: string;
}) {
  const [query, setQuery] = useState("");
  const { suggestions, isFetching } = useAddressSearch({ query });

  const showList = query.trim().length > 0;

  const handlePick = (s: AddressSuggestion) => {
    onSelect(pinFromSuggestion(s));
    setQuery("");
  };

  return (
    <div className={cn("relative", className)}>
      <div className="relative flex h-[42px] items-center bg-withus-white shadow-sm">
        <SearchIcon className="pointer-events-none absolute left-3 size-4 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a street address"
          className="size-full bg-transparent pr-3 pl-9 text-sm text-withus-dark-blue outline-none placeholder:text-muted-foreground"
        />
      </div>

      {showList ? (
        <div className="absolute top-[calc(100%+4px)] left-0 z-20 max-h-72 w-full overflow-y-auto bg-withus-white shadow-md">
          {isFetching && suggestions.length === 0 ? (
            <div className="flex items-center justify-center p-4">
              <Spinner className="text-muted-foreground" />
            </div>
          ) : null}
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => handlePick(s)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-withus-dark-blue/5"
            >
              <span className="min-w-0 truncate text-sm text-withus-dark-blue">
                {s.fullAddress}
              </span>
              <span className="shrink-0 text-sm font-semibold text-withus-red">Add</span>
            </button>
          ))}
          {!isFetching && suggestions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">No matching addresses</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
